import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, AlertTriangle, ExternalLink, Power, PowerOff, BookOpen } from "lucide-react";
import { reloadAdsenseConfig } from "../lib/adsense";
import AdSenseRevenueSection from "./AdSenseRevenueSection";
import AdSenseAlertsSection from "./AdSenseAlertsSection";
import YieldComparisonSection from "./YieldComparisonSection";
import RateCardPanel from "./RateCardPanel";

const SLOT_DEFINITIONS = [
  { key: "homeBanner",    label: "🏠 Bannière Home",            desc: "Sous le hero de la page d'accueil. Format paysage responsive (728x90, 970x250…).", recommended: "Display responsive" },
  { key: "inFeed",        label: "📜 In-feed (entre les vidéos)", desc: "Insérée toutes les 5 vidéos dans Explore et profils artistes.", recommended: "In-feed (Fluid)" },
  { key: "sidebar",       label: "📐 Sidebar droite (desktop)",   desc: "Bloc vertical sticky 300×600 dans les pages de contenu.", recommended: "Display vertical / 300×600" },
  { key: "trackBottom",   label: "🎬 Bas de page Track",         desc: "Sous le lecteur vidéo. Très haut CTR sur la lecture.", recommended: "Display rectangle 336×280" },
  { key: "articleInline", label: "📰 In-article (long format)",  desc: "Au milieu de pages longues (Confidentialité, CGU, About).", recommended: "In-article (Fluid)" },
];

const inp = "w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-orange/60 font-mono";

export const AdminAdSensePanel = () => {
  const [enabled, setEnabled] = useState(false);
  const [slots, setSlots] = useState({});
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = () => api.get("/adsense/config").then(({ data }) => {
    setEnabled(!!data.enabled);
    setSlots(data.slots || {});
    setLoaded(true);
  });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/adsense", { enabled, slots });
      await reloadAdsenseConfig();
      toast.success(enabled ? "AdSense activé sur le site ✨" : "Configuration enregistrée");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setBusy(false); }
  };

  const toggleEnabled = async () => {
    const next = !enabled;
    setEnabled(next);
    setBusy(true);
    try {
      await api.post("/admin/adsense", { enabled: next });
      await reloadAdsenseConfig();
      toast.success(next ? "AdSense ACTIVÉ — pubs visibles sur le site" : "AdSense DÉSACTIVÉ");
    } catch {
      setEnabled(!next);
      toast.error("Erreur");
    } finally { setBusy(false); }
  };

  const filledCount = SLOT_DEFINITIONS.filter((s) => slots[s.key]).length;

  return (
    <div className="space-y-6" data-testid="admin-adsense-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-black">Google AdSense</h2>
          <p className="text-xs text-white/50">
            Compte : <code className="text-brand-orange">ca-pub-7493177636919221</code> ·
            <a href="https://www.google.com/adsense/" target="_blank" rel="noopener noreferrer"
              className="text-brand-orange hover:underline ml-1 inline-flex items-center gap-1">
              Ouvrir le dashboard Google <ExternalLink size={11} />
            </a>
          </p>
        </div>
        <button
          onClick={toggleEnabled}
          disabled={busy || !loaded}
          className={`px-4 py-2 rounded-xl text-sm font-bold inline-flex items-center gap-2 transition ${
            enabled
              ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
              : "bg-white/5 text-white/60 border border-white/10 hover:bg-white/10"
          }`}
          data-testid="adsense-master-toggle"
        >
          {enabled ? <><Power size={14} /> AdSense ACTIVÉ</> : <><PowerOff size={14} /> AdSense désactivé</>}
        </button>
      </div>

      {/* Status bar */}
      <div className={`rounded-2xl border p-4 ${enabled ? "border-emerald-500/30 bg-emerald-500/5" : "border-brand-yellow/30 bg-brand-yellow/5"}`}>
        {enabled ? (
          <p className="text-sm text-emerald-200">
            ✅ Les annonces AdSense sont diffusées sur le site (pour les emplacements dont le Slot ID est rempli).
            <span className="text-emerald-300/70"> {filledCount} / {SLOT_DEFINITIONS.length} emplacements configurés.</span>
          </p>
        ) : (
          <p className="text-sm text-brand-yellow">
            ⚠️ AdSense est désactivé. Active-le une fois ton compte AdSense approuvé par Google.
            Tu peux dès maintenant configurer les Slot IDs ci-dessous.
          </p>
        )}
      </div>

      {/* DASHBOARD REVENUS — connexion API AdSense */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="font-display text-lg font-black">📊 Revenus en temps réel</span>
        </div>
        <AdSenseRevenueSection />
      </div>

      {/* ALERTES INTELLIGENTES — anomaly detection + email */}
      <AdSenseAlertsSection />

      {/* YIELD MANAGEMENT — comparaison Voamada Ads vs AdSense */}
      <YieldComparisonSection />

      {/* RATE CARD — tarifs publics dynamiques */}
      <RateCardPanel />

      {/* Tutoriel */}
      <details className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
        <summary className="cursor-pointer text-sm font-bold inline-flex items-center gap-2"><BookOpen size={14} /> Comment récupérer un Slot ID ?</summary>
        <ol className="list-decimal pl-5 mt-3 space-y-1.5 text-sm text-white/75">
          <li>Va sur <a className="text-brand-orange hover:underline" href="https://www.google.com/adsense/" target="_blank" rel="noopener noreferrer">google.com/adsense</a></li>
          <li>Menu de gauche → <strong>Annonces</strong> → onglet <strong>Par unité d'annonce</strong></li>
          <li>Clique <strong>+ Créer une nouvelle unité d'annonce</strong></li>
          <li>Choisis le format (Display, In-feed, In-article…) selon le tableau ci-dessous</li>
          <li>Donne un nom (ex : <code>voamada-home-banner</code>) → Créer</li>
          <li>Copie le <strong>data-ad-slot</strong> (chaîne de chiffres, ex : <code>1234567890</code>) et colle-le dans le champ correspondant ci-dessous</li>
          <li>Enregistre puis active AdSense en haut</li>
        </ol>
      </details>

      {/* Slots */}
      <div className="space-y-3">
        {SLOT_DEFINITIONS.map((s) => (
          <div key={s.key} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 grid md:grid-cols-[1fr_280px] gap-4 items-start" data-testid={`adsense-slot-row-${s.key}`}>
            <div>
              <p className="font-bold text-sm">{s.label}</p>
              <p className="text-xs text-white/55 mt-0.5">{s.desc}</p>
              <p className="text-[11px] text-brand-orange mt-1">Format recommandé : <strong>{s.recommended}</strong></p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/45 font-bold block mb-1">Slot ID</label>
              <input
                value={slots[s.key] || ""}
                onChange={(e) => setSlots({ ...slots, [s.key]: e.target.value.replace(/\D/g, "") })}
                placeholder="ex: 1234567890"
                className={inp}
                data-testid={`adsense-slot-input-${s.key}`}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={busy} className="btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-50" data-testid="adsense-save-btn">
          <Save size={14} /> {busy ? "Enregistrement…" : "Enregistrer"}
        </button>
        <p className="text-xs text-white/45 inline-flex items-center gap-1.5">
          <AlertTriangle size={12} /> Les Slot IDs sont des chiffres uniquement (sans <code>ca-pub-</code>)
        </p>
      </div>
    </div>
  );
};

export default AdminAdSensePanel;
